import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Loader, Message } from 'semantic-ui-react';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';
import LaunchContextService from '../services/LaunchContextService';
import { CONTENT_MODE, PLAYER_TYPES } from '../managers/ContentManager';
import { ASSIGNMENT_STATUS } from '../managers/AssignmentManager';
import AssignmentService from '../services/AssignmentService';
import ImageService from '../services/ImageService';
import PresentationOptionsService from '../services/PresentationOptionsService';
import { register } from '../i18n';

import '../css/PlayerLaunch.less';

const t = register('PlayerLaunch');

export default
@inject('assignmentManager', 'classroomManager', 'contentManager', 'courseManager', 'userManager')
@observer
class PlayerLaunch extends Component {
  constructor(props) {
    super(props);

    this.DocReaderModal = SatCoreComponent('DocReaderModal');
    this.FileViewerModal = SatCoreComponent('FileViewerModal');
    this.FullscreenModal = SatCoreComponent('FullscreenModal');
    this.LearnosityPlayerModal = SatCoreComponent('LearnosityPlayerModal');
    this.LearnosityScoringModal = SatCoreComponent('LearnosityScoringModal');
    this.PresentationOptionsModal = SatCoreComponent('PresentationOptionsModal');
    
    this.state = {
      error: null,
      fallbackRoute: '/',
      isLoading: true,
      playerState: null,
      presentationOptionsContext: null,
      presentationOptionsOpen: false
    };
  }

  componentDidMount() {
    this.bootstrapLaunchContext();
  }

  bootstrapLaunchContext = async () => {
    const { location, contentManager, assignmentManager, classroomManager, courseManager, userManager } = this.props;
    const params = new URLSearchParams(location?.search);
    const launchId = params.get('playerLaunchId');

    if (!launchId) {
      this.setState({
        error: 'Missing playerLaunchId parameter. Please restart the launch workflow.',
        isLoading: false
      });
      return;
    }

    const context = LaunchContextService.consumeContext(launchId);
    if (!context) {
      this.setState({
        error: 'Launch information has expired. Please retry from the original tab.',
        isLoading: false
      });
      return;
    }

    // Check if we need to create a new presentation
    if (context.createNewPresentation) {
      try {
        // Create the assignment
        await assignmentManager.presentCreateAssignment(
          context.contentItemId,
          context.courseResourceElementId,
          context.classroomId,
          context.courseContentItemId
        );

        const assignment = assignmentManager.getLastAddedAssignment();
        if (!assignment) {
          throw new Error('Failed to create presentation');
        }

        // Handle activity starting if needed
        const activityId = assignment.activityId || assignment.id;
        const activityInstanceId = assignment.activityInstanceId || assignment.id;
        
        if (ASSIGNMENT_STATUS.getCheckableStatuses().includes(assignment.status)) {
          const isReady = await assignmentManager.checkActivity(activityId);
          if (isReady) {
            const isOk = await assignmentManager.startActivity(activityInstanceId, activityId, false);
            if (isOk === false) {
              this.setState({
                error: 'Failed to start activity',
                isLoading: false
              });
              return;
            }
          } else {
            this.setState({
              error: 'Activity is not ready',
              isLoading: false
            });
            return;
          }
        }

        // Set teacher presenting state
        contentManager.setIsTeacherPresenting(true);
        contentManager.setPresentationContext({
          activityId,
          activityInstanceId,
          assignmentId: assignment.id,
          classroomId: context.classroomId
        });
        if (context.classroomId) {
          classroomManager.setCurrentClassroomId(context.classroomId);
        }

        // Get player options
        const option = await contentManager.getOptionsForStudentAssessment(
          assignment,
          window.location.origin,
          userManager.isTeacher,
          userManager.userId
        );

        if (!option || !option.playerType || !option.viewUrl) {
          throw new Error('Failed to get player options');
        }

        const { playerType, viewUrl, isFlowpaper } = option;
        const contentImageUrl = ImageService.getImageUrl(assignment);
        const { resourceWebTitle, resourceWebSubtitle } = AssignmentService.getAssignmentTitleAndDescription(assignment, false);

        // Create player state
        const playerState = {
          playerType,
          contentUrl: viewUrl,
          contentImageUrl,
          contentName: resourceWebTitle,
          contentSubtitle: resourceWebSubtitle,
          contentMode: CONTENT_MODE.TEACHER_PRESENT,
          isFlowpaper,
          assignmentId: assignment.id,
          contentItemId: assignment.contentItemId,
          previewContentType: assignment.contentItemEntityTypeId || assignment.entityTypeId,
          previewContentItemId: assignment.id,
          resourceName: assignment.name,
          instruction: assignment.instruction || assignment.activityInstruction || '',
          activityId,
          activityInstanceId,
          classroomId: context.classroomId,
          grade: assignment.grade
        };

        // Add session ID for docreader if needed
        if (playerType === PLAYER_TYPES.DOCREADER_VIEWER) {
          playerState.sessionId = contentManager.currentflowPaperSessionId;
          playerState.docReaderUrl = viewUrl;
        }

        // Set content manager state if needed
        if (playerState.playerType === PLAYER_TYPES.LESSON_PLAYER) {
          contentManager.setLessonPlayerShowing(true);
        }
        if (playerState.contentUrl) {
          await contentManager.configPlayerWindow(playerState.playerType, window, () => {});
        }
        if (playerState.sessionId) {
          contentManager.setCurrentFlowPaperSessionId(playerState.sessionId);
        }
        if (playerState.docReaderUrl) {
          contentManager.setDocReaderUrl(playerState.docReaderUrl);
        }

        this.setState({
          fallbackRoute: context.meta?.sourceRoute || '/',
          isLoading: false,
          playerState
        });
      } catch (error) {
        console.error('Error creating presentation:', error);
        this.setState({
          error: error instanceof Error ? error.message : 'Failed to create presentation',
          isLoading: false
        });
      }
      return;
    }

    // Handle existing player state
    if (!context.playerState) {
      this.setState({
        error: 'Launch information has expired. Please retry from the original tab.',
        isLoading: false
      });
      return;
    }

    const { playerState } = context;

    if (playerState.contentMode === CONTENT_MODE.TEACHER_PRESENT) {
      contentManager.setIsTeacherPresenting(true);
      contentManager.setPresentationContext({
        activityId: playerState.activityId,
        activityInstanceId: playerState.activityInstanceId,
        assignmentId: playerState.assignmentId,
        classroomId: playerState.classroomId || context.presentationContext?.classroomId || null
      });
    }
    if (playerState.classroomId) {
      classroomManager.setCurrentClassroomId(playerState.classroomId);
    }

    // Set content manager state if needed
    if (playerState.playerType === PLAYER_TYPES.LESSON_PLAYER) {
      contentManager.setLessonPlayerShowing(true);
    }
    if (playerState.contentUrl) {
      await contentManager.configPlayerWindow(playerState.playerType, window, () => {});
    }
    if (playerState.sessionId) {
      contentManager.setCurrentFlowPaperSessionId(playerState.sessionId);
    }
    if (playerState.docReaderUrl) {
      contentManager.setDocReaderUrl(playerState.docReaderUrl);
    }

    this.setState({
      fallbackRoute: context.meta?.sourceRoute || '/',
      isLoading: false,
      playerState
    });
  }

  handleClose = () => {
    const { history, location, contentManager } = this.props;
    const { fallbackRoute, playerState } = this.state;
    const params = new URLSearchParams(location?.search);
    const launchedViaParam = params.has('playerLaunchId');

    const wasPresenting = contentManager.isTeacherPresenting
      || playerState?.contentMode === CONTENT_MODE.TEACHER_PRESENT;

    if (wasPresenting) {
      PresentationOptionsService.openIfPresenting({
        activityId: playerState?.activityId,
        activityInstanceId: playerState?.activityInstanceId || playerState?.assignmentId,
        assignmentId: playerState?.assignmentId,
        classroomId: playerState?.classroomId,
        wasPresenting: true,
        useDialogManager: false,
        onLocalOpen: (context) => {
          // Keep the presentation player mounted until the options dialog is up
          // so the dialog replaces it instead of appearing after the lesson exits.
          this.setState({
            presentationOptionsContext: context,
            presentationOptionsOpen: true
          });
        }
      });
      return;
    }

    if (launchedViaParam) {
      window.close();
      return;
    }

    if (window.opener) {
      window.close();
      return;
    }

    history.replace(fallbackRoute || '/');
  }

  handlePresentationOptionsMount = () => {
    if (this.state.playerState) {
      this.setState({ playerState: null });
    }
  }

  handlePresentationOptionsClose = () => {
    const { history, location } = this.props;
    const { fallbackRoute } = this.state;
    const params = new URLSearchParams(location?.search);
    const launchedViaParam = params.has('playerLaunchId');

    PresentationOptionsService.resetOpenPending();
    const { contentManager } = this.props;
    contentManager.clearPresentationContext();
    contentManager.setIsTeacherPresenting(false);

    this.setState({
      presentationOptionsContext: null,
      presentationOptionsOpen: false
    });

    if (launchedViaParam || window.opener) {
      window.close();
      return;
    }

    history.replace(fallbackRoute || '/');
  }

  renderError = () => {
    const { error } = this.state;
    return (
      <Message
        content={error || 'Unable to load player.'}
        negative />
    );
  }

  renderLessonPlayer = () => {
    const { FullscreenModal } = this;
    const { userManager } = this.props;
    const { playerState } = this.state;

    return (
      <div className='course-content'>
        <FullscreenModal
          className='lesson-player'
          closeIframeCallback={this.handleClose}
          contentImageUrl={playerState.contentImageUrl}
          contentName={playerState.contentName}
          instruction={playerState.instruction}
          isTeacher={userManager.isTeacher}
          page='lesson-player'
          url={playerState.contentUrl} />
      </div>
    );
  }

  renderFileViewer = () => {
    const { FileViewerModal } = this;
    const { userManager } = this.props;
    const { playerState } = this.state;

    return (
      <div className='course-content'>
        <FileViewerModal
          assignmentId={playerState.assignmentId}
          closeModalCallback={this.handleClose}
          contentImageUrl={playerState.contentImageUrl}
          contentMode={playerState.contentMode}
          contentName={playerState.contentName}
          contentType={playerState.previewContentType}
          instruction={playerState.instruction}
          isFlowpaper={playerState.isFlowpaper}
          isTeacher={userManager.isTeacher}
          page='file-viewer'
          resourceName={playerState.resourceName}
          url={playerState.contentUrl} />
      </div>
    );
  }

  renderDocreaderViewer = () => {
    const { DocReaderModal } = this;
    const { userManager, contentManager } = this.props;
    const { playerState } = this.state;

    return (
      <div className='course-content'>
        <DocReaderModal
          assignmentId={playerState.assignmentId}
          closeModalCallback={this.handleClose}
          contentImageUrl={playerState.contentImageUrl}
          contentMode={playerState.contentMode}
          contentName={playerState.contentName}
          contentType={playerState.previewContentType}
          instruction={playerState.instruction}
          isFlowpaper={playerState.isFlowpaper}
          isTeacher={userManager.isTeacher}
          page='docreader-viewer'
          resourceName={playerState.resourceName}
          sessionId={playerState.sessionId || contentManager.currentflowPaperSessionId}
          url={playerState.contentUrl} />
      </div>
    );
  }

  renderLearnosityPlayer = () => {
    const { LearnosityPlayerModal } = this;
    const { userManager, history } = this.props;
    const { playerState } = this.state;

    return (
      <div className='course-content'>
        <LearnosityPlayerModal
          assignmentId={playerState.assignmentId}
          closeModalCallback={this.handleClose}
          contentImageUrl={playerState.contentImageUrl}
          contentItemId={playerState.contentItemId}
          contentMode={playerState.contentMode}
          contentName={playerState.contentName}
          contentSubtitle={playerState.contentSubtitle}
          history={history}
          instruction={playerState.instruction}
          isTeacher={userManager.isTeacher}
          page='learnosity-player'
          resourceName={playerState.resourceName} />
      </div>
    );
  }

  renderLearnosityScoringModal = () => {
    const { LearnosityScoringModal } = this;
    const { userManager, history } = this.props;
    const { playerState } = this.state;

    return (
      <div className='course-content'>
        <LearnosityScoringModal
          activityInstanceId={playerState.assignmentId}
          closeModalCallback={this.handleClose}
          contentImageUrl={playerState.contentImageUrl}
          contentItemId={playerState.contentItemId}
          contentSubTitle={playerState.contentSubtitle}
          contentTitle={playerState.contentName}
          grade={playerState.grade}
          history={history}
          instruction={playerState.instruction}
          isTeacher={userManager.isTeacher}
          page='learnosity-scoring'
          resourceName={playerState.resourceName}
          studentName={playerState.studentName || `${userManager.firstName} ${userManager.lastName}`} />
      </div>
    );
  }

  render() {
    const {
      error, isLoading, playerState, presentationOptionsContext, presentationOptionsOpen
    } = this.state;
    const { PresentationOptionsModal } = this;

    const optionsModal = presentationOptionsOpen ? (
      <PresentationOptionsModal
        activityId={presentationOptionsContext?.activityId}
        activityInstanceId={presentationOptionsContext?.activityInstanceId}
        assignmentId={presentationOptionsContext?.assignmentId}
        classroomId={presentationOptionsContext?.classroomId}
        key='presentation-options'
        onClose={this.handlePresentationOptionsClose}
        onMount={this.handlePresentationOptionsMount}
        open={presentationOptionsOpen} />
    ) : null;

    if (presentationOptionsOpen) {
      const { playerType } = playerState || {};
      let playerNode = null;
      if (playerState && !isLoading && !error) {
        switch (playerType) {
          case PLAYER_TYPES.LESSON_PLAYER:
            playerNode = this.renderLessonPlayer();
            break;
          case PLAYER_TYPES.FILE_VIEWER:
            playerNode = this.renderFileViewer();
            break;
          case PLAYER_TYPES.DOCREADER_VIEWER:
            playerNode = this.renderDocreaderViewer();
            break;
          case PLAYER_TYPES.LEARNOSITY_PLAYER:
            playerNode = this.renderLearnosityPlayer();
            break;
          case PLAYER_TYPES.LEARNOSITY_SCORING_PLAYER:
            playerNode = this.renderLearnosityScoringModal();
            break;
          default:
            playerNode = null;
        }
      }
      return (
        <div className='PlayerLaunch'>
          <div key='presentation-player-slot'>
            {playerNode}
          </div>
          {optionsModal}
        </div>
      );
    }

    if (isLoading) {
      const message = playerState ? t('preparingPlayer', 'Preparing player…') : t('creatingPresentation', 'Creating presentation…');
      return (
        <div className='PlayerLaunch'>
          <div className='player-launch-loading'>
            <div className='loading-message-container'>
              <Loader active inline size='large' />
              <span>{message}</span>
            </div>
          </div>
        </div>
      );
    }

    if (error || !playerState) {
      return (
        <div className='PlayerLaunch'>
          {this.renderError()}
        </div>
      );
    }

    const { playerType } = playerState;

    return (
      <div className='PlayerLaunch'>
        {(() => {
          switch (playerType) {
            case PLAYER_TYPES.LESSON_PLAYER:
              return this.renderLessonPlayer();
            case PLAYER_TYPES.FILE_VIEWER:
              return this.renderFileViewer();
            case PLAYER_TYPES.DOCREADER_VIEWER:
              return this.renderDocreaderViewer();
            case PLAYER_TYPES.LEARNOSITY_PLAYER:
              return this.renderLearnosityPlayer();
            case PLAYER_TYPES.LEARNOSITY_SCORING_PLAYER:
              return this.renderLearnosityScoringModal();
            default:
              return this.renderError();
          }
        })()}
      </div>
    );
  }
}

SatCoreRegister('PlayerLaunch', PlayerLaunch);
